
import {
  Vue, Component, Watch,
} from 'vue-property-decorator';
import { Route } from 'vue-router';
import Loading from '@/components/Loading.vue';
import PageHeader from '@/components/PageHeader.vue';
import LeadStatus from '@/components/LeadStatus.vue';
import LeadWorkflowTasks from '@/components/LeadWorkflowTasks.vue';
import LeadUsers from '@/components/LeadUsers.vue';
import LeadCustomer from '@/components/LeadCustomer.vue';
import LeadEturnityDebug from '@/components/LeadEturnityDebug.vue';
import EturnityWaitingDialog from '@/components/EturnityWaitingDialog.vue';
import LeadDetails from '@/components/LeadDetails.vue';
import LeadPayments from '@/components/LeadPayments.vue';
import LeadSupply from '@/components/LeadSupply.vue';
import LeadWorkflow from '@/components/LeadWorkflow.vue';
import LeadLogs from '@/components/LeadLogs.vue';
import LeadAttachments from '@/components/LeadAttachments.vue';
import LeadSharedNotes from '@/components/LeadSharedNotes.vue';
import LeadPrivateNotes from '@/components/LeadPrivateNotes.vue';
import LeadOperationNotes from '@/components/LeadOperationNotes.vue';
import LeadActions from '@/components/LeadActions.vue';
import {
  PermissionCode, User, LeadStatus as TLeadStatus, Lead as TLead, ProjectsPool,
} from '@/types';
import { leadIdFromRouteCode } from '@/helpers/leadCode';

@Component({
  components: {
    Loading,
    PageHeader,
    LeadStatus,
    LeadWorkflowTasks,
    LeadUsers,
    LeadCustomer,
    LeadEturnityDebug,
    EturnityWaitingDialog,
    LeadDetails,
    LeadPayments,
    LeadSupply,
    LeadWorkflow,
    LeadSharedNotes,
    LeadPrivateNotes,
    LeadOperationNotes,
    LeadLogs,
    LeadAttachments,
    LeadActions,
  },
})
export default class Lead extends Vue {
  updated = false
  firstTimeUpdating = true
  canRender = false

  // tab: number | null = null

  breadcrumbs = [
    {
      text: 'Elenco',
      exact: true,
      to: { name: 'Elenco' },
    },
  ]

  id: number | null = null

  leaveDialog = false

  next: Function | null = null

  set tab(tab) {
    this.$router.replace({ query: { ...this.$route.query, tab } });
  }

  get tab() {
    return this.$route.query.tab;
  }

  get lead(): TLead {
    return this.$store.getters.currentLead;
  }

  get version(): number | false {
    return this.lead
      && 'version' in this.lead
      && this.lead.version ? this.lead.version : false;
  }

  get pool(): ProjectsPool {
    return this.$store.getters.currentPool;
  }

  get children(): ProjectsPool {
    return this.$store.getters.currentChildren;
  }

  get status(): TLeadStatus | null {
    return this.lead && 'status' in this.lead ? this.lead.status : null;
  }

  get updating(): boolean {
    return this.$store.getters.isProjectUpdating;
  }

  get user(): User {
    return this.$store.getters.currentUser;
  }

  get isSuperUser(): boolean {
    return this.user && this.user.isSuperUser;
  }

  get isRequester(): boolean {
    return this.user && this.lead && this.user.id === this.lead.requesterId;
  }

  get userPermissions(): PermissionCode[] {
    return this.$store.getters.currentUserPermissions;
  }

  get leadType() {
    switch (this.lead.type) {
      case 'installatore-figlia':
        return 'Lead figlia generica';

      case 'elettrica-figlia':
        return 'Lead figlia elettrica';

      case 'termica-figlia':
        return 'Lead figlia termica';

      default:
        break;
    }

    return this.lead.type;
  }

  get isChild() {
    return ['installatore-figlia', 'elettrica-figlia', 'termica-figlia'].includes(this.lead?.type);
  }

  get showLeadSupply() {
    // Il box con l'oggetto della fornitura viene mostrato solo
    // se l'utente ha almeno uno dei tre permessi necessari per
    // vedere i dati mostrati (e se è una lead padre o figlia elettrica/termica).
    return (this.hasPermission('totalContractValue.show') || this.hasPermission('targetDate.show') || this.hasPermission('supply.show'))
      && (!this.isChild || ['elettrica-figlia', 'termica-figlia'].includes(this.lead?.type));
  }

  @Watch('lead')
  onLeadChange(val: TLead) {
    console.log('Lead: onLeadChange() val =', val); // eslint-disable-line no-console
    console.log('Lead: onLeadChange() _accessViaConnection =', val?._accessViaConnection); // eslint-disable-line no-console

    if (!val) return;

    // Verifica se l'accesso è avvenuto tramite Connection
    // In questo caso, l'utente non dovrebbe poter aprire direttamente la vista Lead
    // eslint-disable-next-line no-underscore-dangle
    if ('_accessViaConnection' in val && val._accessViaConnection === true) {
      console.warn('Lead: onLeadChange() - Accesso negato: utente ha accesso solo tramite Connection'); // eslint-disable-line no-console
      // Redirect alla vista Connections
      this.$router.replace({ name: 'Connections' });
      return;
    }

    // L'utente ha accesso legittimo, può renderizzare la vista
    this.canRender = true;

    // Inserisce il codice della lead nei breadcrumbs
    if (this.lead && this.lead.code) {
      this.breadcrumbs = [
        {
          text: 'Elenco',
          exact: true,
          to: { name: 'Elenco' },
        },
        {
          text: `Lead ${this.lead.code} (${this.leadType})`,
          exact: true,
          to: { name: 'Lead' },
        },
      ];
    }
  }

  @Watch('$route')
  async onRouteChange(val: Route, oldVal: Route) {
    console.log('Lead: watch.$route() val =', val); // eslint-disable-line no-console
    console.log('Lead: watch.$route() oldVal =', oldVal); // eslint-disable-line no-console

    if (val.name === 'Lead' && this.id !== leadIdFromRouteCode(val.params.code)) {
      console.log('Lead: watch.$route() val.params.code =', val.params.code); // eslint-disable-line no-console

      console.log('Lead: watch.$route() la lead è cambiata, selezione la nuova lead'); // eslint-disable-line no-console
      this.canRender = false;
      this.id = leadIdFromRouteCode(val.params.code);
      this.firstTimeUpdating = true;
      await this.$store.dispatch('selectLead', { id: this.id });
    }
  }

  @Watch('updating')
  onFirstTimeUpdatingChange(val: boolean, oldVal: boolean) {
    if (!val && oldVal) {
      this.firstTimeUpdating = false;
    }
  }

  async created() {
    console.log('Lead: created()'); // eslint-disable-line no-console

    if (this.id !== leadIdFromRouteCode(this.$route.params.code)) {
      console.log('Lead: created() this.$route.params.code =', this.$route.params.code); // eslint-disable-line no-console

      console.log('Lead: created() la lead è cambiata, selezione la nuova lead'); // eslint-disable-line no-console
      this.canRender = false;
      this.id = leadIdFromRouteCode(this.$route.params.code);
      await this.$store.dispatch('selectLead', { id: this.id });
    }
  }

  mounted() {
    console.log('Lead: mounted()'); // eslint-disable-line no-console

    // Aggiorna la data/ora dell'ultima attività dell'utente corrente
    this.$store.dispatch('updateLastActivity');
  }

  async beforeDestroy() {
    console.log('Lead: beforeDestroy()'); // eslint-disable-line no-console

    console.log('Lead: beforeDestroy() deseleziona la lead selezionata'); // eslint-disable-line no-console
    this.$store.dispatch('unselectLead');
  }

  hasPermission(codename: PermissionCode) {
    if (!this.user) return false;
    if (this.user.isSuperUser) return true;

    return this.userPermissions.includes(codename);
  }

  onGoto(where: string) {
    console.log('Lead: onGoto() where =', where); // eslint-disable-line no-console

    switch (where) {
      case 'opNotes': {
        this.$router.replace({ query: { ...this.$route.query, tab: 'opNotes' } });
        break;
      }

      default:
        break;
    }
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    if (!this.updated) {
      // this.$store.dispatch('unselectLead');
      next();
    }

    this.next = next;

    this.leaveDialog = true;

    next(false);
  }

  leave() {
    // Deseleziona la lead
    // this.$store.dispatch('unselectLead');
    // Prosegue verso la prossima pagina (se definita)
    if (this.next) return this.next();
    // Altrimenti va all'elenco nonostante le eventuali modifiche
    this.updated = false;
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return this.$router.replace({ name: 'Elenco' }, () => {});
  }
}
